<template>
  <div id="templates">
    <Cms
      v-if="query.theme === 'cms'"
      :categories="categories"
      :hits="hits"
      :updateQuery="updateQuery"
      :hasNoResult="hasNoResult"
      :nbHits="nbHits"
      :nbPages="nbPages"
    ></Cms>
    <Web
      v-else-if="query.theme === 'web'"
      :categories="categories"
      :hits="hits"
      :query="query"
      :updateQuery="updateQuery"
    ></Web>
    <Default
      v-else
      :categories="categories"
      :isLoading="isLoading"
      :isEmpty="isEmpty"
      :items="hits"
      :updateQuery="updateQuery"
      :nbPages="nbPages"
      :nbHits="nbHits"
    ></Default>
  </div>
</template>
<script>
import algoliasearch from 'algoliasearch';
import { mapState } from 'vuex';

import Cms from '@/components/routes/themes/cms/Cms';
import Web from '@/components/routes/themes/web/Web';
import Default from '@/components/routes/themes/default/Default';

export default {
  name: 'Home',
  components: {
    Cms,
    Web,
    Default,
  },
  methods: {
    handleSearch() {
      const { q, page, category, time, index, hitsPerPage, theme } = this.query;
      const { ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_API_KEY } = this;
      const client = algoliasearch(ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_API_KEY);
      const indexed = client.initIndex(index);
      if (theme !== 'web' && !(q.trim())) {
        this.hits = [];
        this.nbHits = 0;
        this.nbPages = 0;
        return;
      }
      try {
        this.isLoading = true;
        const facetFilters = category && category !== this.$t('all') ? [(`category:${category}`)] : [];
        // TODO: Remove query 'category' if 'All' selected
        const numericFilters = time.value ? [`date_timestamp > ${time.value}`] : [];
        indexed.search(q.trim(), { 
          facetFilters,
          numericFilters,
          page: page-1,
          hitsPerPage,
         }).then(({ hits, nbHits, nbPages }) => {
          this.hits = hits;
          this.nbHits = nbHits;
          this.nbPages = nbPages;
          this.isEmpty = nbHits === 0;
          this.isLoading = false;
          for (const func of this.callStack) {
            func();
          }
          this.callStack = [];
        })
      } catch (error) {
        console.log(error);
      }
    },
    updateQuery({
      q = this.$route.query.q,
      category = this.$route.query.category,
      time = this.$route.query.time,
      page = this.$route.query.page,
      hitsPerPage = this.$route.query.hitsPerPage,
    }) {
      const { query, params } = this.$route;
      this.$router.push({
        path: '/',
        params,
        query: {
          ...query,
          category: category !== this.$t('all') ? category : undefined,
          time: time !== 'all' ? time : undefined,
          page: page !== 1 ? page : undefined,
          hitsPerPage: hitsPerPage || undefined,
          q: q !== '' ? q : undefined,
        },
      }).catch(() => {});
    },
    scrollToTop() {
      window.scrollTo(0,0);
    },
    sendContainerHeight() {
      const containerElement = document.querySelector('.firma-container');
      if(containerElement) {
        this.$nextTick(() => {
          const containerHeight = containerElement.clientHeight;
          parent.postMessage({ frameHeight: containerHeight }, '*');
        })
      }
    },
    updateQueryByMessage(event){
      if (event.data.query) {
        if(this.debounce !== event.data.query) {
          this.immediateSearch = false;
          this.debounce = event.data.query;
          this.queryFromParent = event.data.query;
        }
      }
      if (event.data.pressedKey || event.data.ctaClicked) {
        this.immediateSearch = true;
      }
    }
  },
  computed: {
    ...mapState({
      ALGOLIA_APPLICATION_ID: state => state.env.ALGOLIA_APPLICATION_ID,
      ALGOLIA_SEARCH_API_KEY: state => state.env.ALGOLIA_SEARCH_API_KEY,
      times: state => state.constants.times,
    }),
    hasNoResult() {
      const { hits, isLoading, query } = this;
      return (!hits || !hits.length) && !isLoading && Boolean(query.q)
    },
    debounce: {
      get() {
        return this.query.q
      },
      set(q) {
        if (this.timeout) clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
          this.updateQuery({ q });
        }, 1000)
      }
    }
  },
  data(){
    const { 
      q,
      page,
      hitsPerPage,
      category,
      time,
      index,
      theme,
    } = this.$route.query;
    return {
      query: {
        q: q || '',
        page: page || 1,
        hitsPerPage,
        category: category || this.$t('all'),
        time: time || { text: 'all', value: null},
        index: index || 'dev_cms_german',
        theme,
      },
      timeout: null,
      immediateSearch: false,
      queryFromParent: '',
      nbHits: 0,
      nbPages: 1,
      hits: [],
      isEmpty: false,
      isLoading: false,
      categories: {
        dev_cms_german: [
          'Firmengründung',
          'Magazin',
          'Rechnungswesen',
          'Unternehmensführung'
        ],
        cms: [
          'Firmengründung',
          'Magazin',
          'Rechnungswesen',
          'Unternehmensführung'
        ]        
      },
      callStack: [],
    };
  },
  watch: {
    '$route.query': {
      immediate: true,
      deep: true,
      handler(newVal, oldVal) {
        if(newVal.theme === 'cms' || newVal.theme === 'web') {
          document.querySelector('html').style.overflow = 'hidden';
          this.callStack = [...this.callStack, this.sendContainerHeight]
        }
        if (
          (!oldVal) ||
          (newVal.q !== oldVal.q) ||
          (newVal.page !== oldVal.page) ||
          (newVal.hitsPerPage !== oldVal.hitsPerPage) ||
          (newVal.time !== oldVal.time) ||
          (newVal.category !== oldVal.category) ||
          (newVal.index !== oldVal.index) ||
          (newVal.theme !== oldVal.theme)
        ) {
          const { times, scrollToTop, categories } = this;
          const { q, page, hitsPerPage, time, category, index, theme } = newVal;
          const initialCategory = newVal.theme === 'web' && categories[index || 'dev_cms_german'][0];
          this.query = {
            q: q || '',
            page: parseInt(page) || 1,
            hitsPerPage: parseInt(hitsPerPage) || undefined,
            time: time ? times.find(item => item.text === time) : { text: 'all', value: null},
            category: category || initialCategory || this.$t('all'),
            index: index || 'dev_cms_german',
            theme,
          }
          this.handleSearch();
          if ((!oldVal && newVal.page) || (newVal.page !== oldVal?.page )) {
            scrollToTop();
          }
        }
      }
    },
    immediateSearch(newVal) {
      if(newVal) {
        const { timeout, updateQuery, queryFromParent } = this;
        clearTimeout(timeout);
        updateQuery({ q: queryFromParent });
      }
    }
  },
  mounted () {
    window.addEventListener('message', this.updateQueryByMessage);
  },
  beforeDestroy () {
    window.removeEventListener('message', this.updateQueryByMessage);
  },
};
</script>